<template>
  <section class="home-banner">
    <div class="home-banner__visual">
      <img
        v-if="backgroundImage"
        :src="backgroundImage"
        :alt="title"
        class="home-banner__image"
      />
      <div v-else class="home-banner__placeholder">
        <div class="home-banner__placeholder-logo">VIXIO</div>
      </div>
    </div>
    <div class="home-banner__content">
      <span v-if="label" class="home-banner__label">{{ label }}</span>
      <h2 class="home-banner__title">{{ title }}</h2>
      <p v-if="subtitle" class="home-banner__subtitle">{{ subtitle }}</p>
      <div v-if="ctaText" class="home-banner__cta-wrap">
        <nuxt-link v-if="isInternalLink" :to="ctaUrl" class="home-banner__cta">
          {{ ctaText }}
        </nuxt-link>
        <a v-else :href="ctaUrl" target="_blank" rel="noopener" class="home-banner__cta">
          {{ ctaText }}
        </a>
      </div>
      <span v-if="eventDate" class="home-banner__date">{{ eventDate }}</span>
    </div>
  </section>
</template>

<script>
export default {
  name: "HomeBanner",
  props: {
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: null,
    },
    description: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: null,
    },
    ctaText: {
      type: String,
      default: null,
    },
    ctaUrl: {
      type: String,
      default: "#",
    },
    backgroundImage: {
      type: String,
      default: null,
    },
    eventDate: {
      type: String,
      default: null,
    },
  },
  computed: {
    isInternalLink() {
      return this.ctaUrl && this.ctaUrl.startsWith("/");
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../assets/styles/helpers/variables";
@import "../../assets/styles/helpers/mixin";

.home-banner {
  display: flex;
  border-radius: 8px;
  overflow: hidden;
  background: $corporateBlue;
  color: $white;
  min-height: 200px;
  margin-bottom: 32px;

  @include below($md) {
    flex-direction: column;
    min-height: auto;
  }

  &__visual {
    width: 260px;
    flex-shrink: 0;
    position: relative;
    overflow: hidden;

    @include below($lg) {
      width: 200px;
    }

    @include below($md) {
      width: 100%;
      height: 140px;
    }
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__placeholder {
    width: 100%;
    height: 100%;
    background: linear-gradient(145deg, lighten($corporateBlue, 8%) 0%, darken($corporateBlue, 4%) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    // Decorative circles
    &::before,
    &::after {
      content: "";
      position: absolute;
      border-radius: 50%;
      border: 1px solid rgba(255, 255, 255, 0.06);
    }

    &::before {
      width: 200px;
      height: 200px;
      top: -60px;
      left: -40px;
    }

    &::after {
      width: 140px;
      height: 140px;
      bottom: -30px;
      right: -20px;
    }
  }

  &__placeholder-logo {
    font-size: 2.4rem;
    font-weight: 800;
    color: rgba(255, 255, 255, 0.12);
    letter-spacing: 0.15em;
    user-select: none;
  }

  &__content {
    flex: 1;
    padding: 28px 32px;
    display: flex;
    flex-direction: column;
    justify-content: center;

    @include below($md) {
      padding: 20px 24px;
    }
  }

  &__label {
    display: inline-block;
    align-self: flex-start;
    font-size: 1.05rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: $brightGreen;
    background: rgba($brightGreen, 0.12);
    padding: 3px 10px;
    border-radius: 3px;
    margin-bottom: 10px;
  }

  &__title {
    font-size: 2rem;
    font-weight: 700;
    line-height: 1.3;
    margin: 0 0 6px;
    color: $white;

    @include below($md) {
      font-size: 1.8rem;
    }
  }

  &__subtitle {
    font-size: 1.35rem;
    line-height: 1.5;
    margin: 0 0 16px;
    color: rgba($white, 0.7);
    max-width: 420px;
  }

  &__cta-wrap {
    margin-bottom: 10px;
  }

  &__cta {
    display: inline-block;
    padding: 9px 22px;
    background: $brightGreen;
    color: $corporateBlue;
    font-size: 1.3rem;
    font-weight: 700;
    text-decoration: none;
    border-radius: 4px;
    transition: background 0.15s ease, transform 0.15s ease;

    &:hover {
      background: darken($brightGreen, 6%);
      transform: translateY(-1px);
    }
  }

  &__date {
    font-size: 1.15rem;
    color: rgba($white, 0.45);
    font-weight: 500;
  }
}
</style>
